import * as THREE from '@phdsoft/three';
import { FeaResult } from './fea-result';
import { FEAElement } from '../models/fea-element';
import { GraphicsMaterials } from 'src/app/digital-twin/graphics/graphics-materials';


export class FeaMeshRenderer {
  private meshLines: THREE.LineSegments[] = [];

  constructor() {}

  public generateMeshLines(feaResult: FeaResult): THREE.LineSegments[] {
    this.dispose(); 
    
    for (const regionId in feaResult.ElementsByRegion) {
      const regionElements = feaResult.ElementsByRegion[regionId] as FEAElement[];
      
      const edgePositions: number[] = [];
      
      for (const element of regionElements) {
        if (!element.RealVisibility) {
          continue; 
        }
        
        const elementEdges = this.extractElementEdges(element);
        
        // Fix RangeError: Use a loop instead of spread operator (...)
        for (let i = 0, len = elementEdges.length; i < len; i++) {
          edgePositions.push(elementEdges[i]);
        }
      }
      
      if (edgePositions.length > 0) {
        const lineSegments = this.createLineSegments(new Float32Array(edgePositions));
        lineSegments.userData.regionId = regionId;
        this.meshLines.push(lineSegments);
      }
    }
    
    return this.meshLines;
  }


  private extractElementEdges(element: FEAElement): number[] {
    const geometry = element.surfaceDesign.geometry as THREE.BufferGeometry;
    const positionAttribute = geometry.getAttribute('position') as THREE.BufferAttribute;
    const elementTopology = element.surfaceDesign.userData.ElementTopology as number[];
    const nodesIds = element.surfaceDesign.userData.NodesIds as number[];
    
    if (!elementTopology || elementTopology.length === 0) {
      console.warn('ElementTopology not available for mesh generation');
      return [];
    }
    
    const edges: number[] = [];
    let vertexIndex = 0;
    
    for (let elemIdx = 0; elemIdx < elementTopology.length; elemIdx++) {
      const topology = elementTopology[elemIdx];
      
      if (topology === undefined) {
        console.warn(`Topology undefined at index ${elemIdx}`);
        continue;
      }
      
      switch (topology) {
        case 2: 
          this.extractTriangleEdges(positionAttribute, vertexIndex, edges);
          vertexIndex += 3;
          break;
          
        case 4: 
          this.extractQuadEdges(positionAttribute, nodesIds, vertexIndex, edges);
          vertexIndex += 6; 
          break;
          
        case 0: 
          const ringSize = this.getTubeRingSize(geometry);
          vertexIndex += ringSize * 2;
          break;
          
        default:
          console.warn(`Unknown topology ${topology} at index ${elemIdx}`);
          break;
      }
    }
    
    return edges;
  }

  private extractTriangleEdges(
    positionAttr: THREE.BufferAttribute, 
    startIdx: number, 
    edges: number[]
  ): void {
    const x0 = positionAttr.getX(startIdx);
    const y0 = positionAttr.getY(startIdx);
    const z0 = positionAttr.getZ(startIdx);
    
    const x1 = positionAttr.getX(startIdx + 1);
    const y1 = positionAttr.getY(startIdx + 1);
    const z1 = positionAttr.getZ(startIdx + 1);
    
    const x2 = positionAttr.getX(startIdx + 2);
    const y2 = positionAttr.getY(startIdx + 2);
    const z2 = positionAttr.getZ(startIdx + 2);

    edges.push(x0, y0, z0, x1, y1, z1);
    edges.push(x1, y1, z1, x2, y2, z2);
    edges.push(x2, y2, z2, x0, y0, z0);
  }

  private extractQuadEdges(
    positionAttr: THREE.BufferAttribute,
    nodesIds: number[],
    startIdx: number,
    edges: number[]
  ): void {
    // Keep original Map logic for corner detection but optimize coordinate access
    const uniqueNodes = new Map<number, {x: number, y: number, z: number}>();
    
    for (let i = 0; i < 6; i++) {
        const idx = startIdx + i;
        const nid = nodesIds[idx];
        if (!uniqueNodes.has(nid)) {
            uniqueNodes.set(nid, {
                x: positionAttr.getX(idx),
                y: positionAttr.getY(idx),
                z: positionAttr.getZ(idx)
            });
        }
    }
    
    const cornerNodeIds = [nodesIds[startIdx + 0], nodesIds[startIdx + 1], nodesIds[startIdx + 2], nodesIds[startIdx + 4]]; 
    const corners = cornerNodeIds.map(id => uniqueNodes.get(id)!);
    
    for (let i = 0; i < 4; i++) {
      const v1 = corners[i];
      const v2 = corners[(i + 1) % 4];
      edges.push(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z);
    }
  }


  private getTubeRingSize(geom: THREE.BufferGeometry): number {
    const p: any = (geom as any).parameters;
    if (p?.radialSegments !== undefined) return (p.radialSegments as number) + 1;
    
    const uv = geom.getAttribute('uv') as THREE.BufferAttribute | undefined;
    if (uv && uv.itemSize >= 2 && uv.count > 1) {
      const u0 = uv.getX(0);
      let count = 1;
      const eps = 1e-6;
      for (let i = 1; i < uv.count; i++) {
        if (Math.abs(uv.getX(i) - u0) < eps) count++;
        else break;
      }
      return count;
    }
    
    return 9;
  }

  private createLineSegments(positions: Float32Array): THREE.LineSegments {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const lineSegments = new THREE.LineSegments(
      geometry, 
      GraphicsMaterials.feaMeshMaterial
    );
    
    lineSegments.name = 'FEAMeshLines';
    return lineSegments;
  }

  public dispose(): void {
    for (const lineSegment of this.meshLines) {
      if (lineSegment.geometry) {
        lineSegment.geometry.dispose();
      }
    }
    this.meshLines = [];
  }

  public getMeshLines(): THREE.LineSegments[] {
    return this.meshLines;
  }
}

